import { apiClient } from './apiClient';

// =====================================================
// Types khớp với backend DTOs (V1.0.23)
// =====================================================

export type ProjectStatus = 'DRAFT' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'ARCHIVED';
export type WorkflowMode = 'PARALLEL' | 'SEQUENTIAL';
export type ProjectMemberRole = 'OWNER' | 'MANAGER' | 'MEMBER' | 'VIEWER';

export interface ProjectListItem {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  departmentId: string | null;
  departmentName: string | null;
  ownerId: string;
  ownerName: string;
  status: ProjectStatus;
  workflowMode: WorkflowMode;
  startDate: string | null;
  endDate: string | null;
  progressPct: number;
  taskCount: number;
  completedTaskCount: number;
  createdAt: string;
}

export interface ProjectMember {
  userId: string;
  fullName: string;
  avatarUrl: string | null;
  position: string | null;
  role: ProjectMemberRole;
  joinedAt: string;
}

export interface ProjectDetail extends Omit<ProjectListItem, 'taskCount' | 'completedTaskCount'> {
  taskCount: number;
  completedTaskCount: number;
  overdueTaskCount: number;
  members: ProjectMember[];
  createdBy: string | null;
  createdByName: string | null;
  /** V1.0.34: Khoa được phép xem project (primary là phần tử đầu) */
  sharedWithDepartments?: Array<{
    id: string;
    code: string;
    name: string;
    groupId?: string | null;
    groupName?: string | null;
    groupColor?: string | null;
  }>;
}

export interface CreateProjectRequest {
  name: string;
  code?: string;
  description?: string;
  color?: string;
  icon?: string;
  departmentId?: string;
  workflowMode?: WorkflowMode;
  startDate?: string;
  endDate?: string;
  initialMemberIds?: string[];
  /** V1.0.34: Khoa phụ ngoài primary departmentId */
  additionalDepartmentIds?: string[];
}

export interface UpdateProjectRequest {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  departmentId?: string;
  status: ProjectStatus;
  workflowMode: WorkflowMode;
  startDate?: string;
  endDate?: string;
  /** V1.0.34: null = không thay đổi, mảng = REPLACE toàn bộ khoa phụ */
  additionalDepartmentIds?: string[];
}

export interface ProjectTaskListItem {
  id: string;
  code: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  dueDate: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  assigneeAvatar: string | null;
  /** V1.0.35: Người giao task (assigner) — hiển thị trong card */
  assignerUserId?: string | null;
  assignerName?: string | null;
  assignerAvatar?: string | null;
  dependencyCount: number;
  isBlocked: boolean;
  priorityLevel?: number;
  importantLevel?: number;
  createdAt: string;
  /** V1.1.1: Task con cũng thuộc dự án → dùng để hiển thị phân cấp, tránh lẫn với task gốc */
  parentTaskId?: string | null;
  parentTaskTitle?: string | null;
}

export interface TaskDependency {
  taskId: string;
  dependsOnTaskId: string;
  dependsOnTaskTitle: string;
  dependsOnTaskStatus: string;
  dependencyType: string;
}

// =====================================================
// Service methods
// =====================================================

export const projectService = {
  async list(params?: {
    status?: ProjectStatus;
    departmentId?: string;
    ownerId?: string;
    myProjectsOnly?: boolean;
    keyword?: string;
  }): Promise<ProjectListItem[]> {
    const { data } = await apiClient.get<ProjectListItem[]>('/projects', { params });
    return data;
  },

  async getById(id: string): Promise<ProjectDetail> {
    const { data } = await apiClient.get<ProjectDetail>(`/projects/${id}`);
    return data;
  },

  async create(req: CreateProjectRequest): Promise<ProjectDetail> {
    const { data } = await apiClient.post<ProjectDetail>('/projects', req);
    return data;
  },

  async update(id: string, req: UpdateProjectRequest): Promise<ProjectDetail> {
    const { data } = await apiClient.patch<ProjectDetail>(`/projects/${id}`, req);
    return data;
  },

  async archive(id: string): Promise<void> {
    await apiClient.post(`/projects/${id}/archive`);
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/projects/${id}`);
  },

  // Members
  async getMembers(projectId: string): Promise<ProjectMember[]> {
    const { data } = await apiClient.get<ProjectMember[]>(`/projects/${projectId}/members`);
    return data;
  },

  async addMember(projectId: string, userId: string, role: ProjectMemberRole): Promise<void> {
    await apiClient.post(`/projects/${projectId}/members`, { userId, role });
  },

  async updateMemberRole(projectId: string, userId: string, role: ProjectMemberRole): Promise<void> {
    await apiClient.patch(`/projects/${projectId}/members/${userId}`, { role });
  },

  async removeMember(projectId: string, userId: string): Promise<void> {
    await apiClient.delete(`/projects/${projectId}/members/${userId}`);
  },

  // Tasks
  async getTasks(projectId: string): Promise<ProjectTaskListItem[]> {
    const { data } = await apiClient.get<ProjectTaskListItem[]>(`/projects/${projectId}/tasks`);
    return data;
  },

  // Dependencies
  async getTaskDependencies(taskId: string): Promise<TaskDependency[]> {
    const { data } = await apiClient.get<TaskDependency[]>(`/projects/tasks/${taskId}/dependencies`);
    return data;
  },

  async addTaskDependency(
    taskId: string, dependsOnTaskId: string, dependencyType = 'FINISH_TO_START'
  ): Promise<void> {
    await apiClient.post(`/projects/tasks/${taskId}/dependencies`, {
      dependsOnTaskId,
      dependencyType,
    });
  },

  async removeTaskDependency(taskId: string, dependsOnTaskId: string): Promise<void> {
    await apiClient.delete(`/projects/tasks/${taskId}/dependencies/${dependsOnTaskId}`);
  },

  async recalcProgress(projectId: string): Promise<void> {
    await apiClient.post(`/projects/${projectId}/recalc-progress`);
  },
};
